// Aggregates stats from Web nodes and charms into one flat Modifiers object (docs/04 §4, §19).
import { B } from '../content/balance';
import { FED_SECRETS } from '../content/secrets';
import { CHARMS, HEXES, NODES, RELICS } from '../content/registry';
import { applyDealMods } from './deals';
import { activeRules } from './reckoning';
import type { BonusMode, MetaState, PatternId, Rarity, RunState, SizeId, Tag } from './types';
import { addTo } from './util';

export type Feature = 'hold' | 'nudge' | 'draft' | 'shop' | 'feed' | 'face' | 'deals' | 'eyes' | 'molt';
/** Gates for which symbols may appear in drafts. */
export type SymbolGate = 'start' | 'awake' | 'rot' | 'hex' | 'eyes' | BonusMode;

export interface Modifiers {
  size: SizeId;
  features: Set<Feature>;
  bonuses: Set<BonusMode>;
  patterns: Set<PatternId>;
  symbolGates: Set<SymbolGate>;

  luck: number;
  luckById: Record<string, number>;
  pityAfter: number;
  hotHandLuck: number;
  perfectPullLuck: number;
  perfectPullAnyInput: boolean;
  perfectPullXMult: number;
  secondChance: boolean;
  loadedFate: boolean;
  favoredLuckPer5: number;
  firstSpinLuck: number;

  peekRows: number;
  holdPerWindow: number;
  nudgePerWindow: number;
  spinsPerWindow: number;
  holdCarryMax: number;
  freeNudgeChance: number;
  longNudge: boolean;
  nudgeSight: boolean;
  heldValue: number;
  deadSpinNudgeRefund: number;

  valueAll: number;
  valueByTag: Partial<Record<Tag, number>>;
  valueById: Record<string, number>;
  pctByTag: Partial<Record<Tag, number>>;
  pctByRarity: Partial<Record<Rarity, number>>;
  patternMultAdd: Partial<Record<PatternId, number>>;
  patternMultAll: number;
  jackpotMultX: number;
  xMult: { source: string; x: number }[];

  payEarlyPct: number;
  compoundPct: number;
  tributeMult: number;
  soulMult: number;
  soulFlatPerTribute: number;
  startCoinsT1: number;
  startTribute: number;

  draftChoices: number;
  rarityBoost: number;
  skipDraftSouls: number;

  charmSlots: number;
  shopCharms: number;
  shopDiscount: number;
  startCommonCharm: boolean;

  // Hunger, moods and feeding (docs/04 §12, §9)
  /** Additive % on Hunger gain (−0.25 = 25% less). */
  hungerGainPct: number;
  /** Additive % on the Hunger that feeding removes. */
  feedPct: number;
  /** Coins per fed symbol = its VALUE × this × par (Doggy Bag). */
  feedValuePar: number;
  /** Coins per fed symbol = rarity tier × this × par (Leftovers). */
  feedTierPar: number;
  ravenousX: number;
  pickyEater: boolean;
  contentLuck: number;
  adoringWindows: number;
  /** ×MULT added for the rest of the window each time the machine eats. */
  eatXMult: number;

  // Rot (docs/04 §11)
  rotAgeAdd: number;
  /** Rot threshold multiplier (Preserves Jar: 2 = rots 2× slower). */
  rotSlow: number;
  compostChance: number;
  rotNever: boolean;

  // ── Phase 3: Reckonings, hexes, deals, bonuses, relics ──
  /** Reckonings happen (the machine is awake and has associates). */
  reckonings: boolean;
  /** Extra appearances added to a symbol's age per landing (Moth Queen candles, Mildew fruit). */
  ageExtraById: Record<string, number>;
  ageExtraByTag: Partial<Record<Tag, number>>;
  /** Symbols without one of these tags pay −50% (Moth Queen). null = off. */
  halveExceptTags: Tag[] | null;
  /** Every ×MULT is square-rooted (Taxman). */
  sqrtXMult: boolean;
  /** Right half of the grid mirrors the left half (Mirror Man). */
  mirror: boolean;
  /** Reels are dark until they stop (Auditor, presentation only). */
  darkReels: boolean;
  /** The Glutton eats one random visible symbol every spin. */
  gluttonEats: boolean;
  /** Spin budget multiplier (Stopwatch: 0.7). */
  spinsMult: number;
  /** Pay-early bonus multiplier (Stopwatch ×3, Receipt Roll ×2). */
  payEarlyX: number;
  payEarlyDisabled: boolean;
  /** Extra tribute multiplier for Reckoning windows (Debt Relief lowers it). */
  reckoningTributeMult: number;
  /** Reels that can't be held or nudged (Seized Reel). */
  lockedReels: Set<number>;
  invertNudge: boolean;
  hidePreview: boolean;
  /** Flat Hunger per spin on top of the usual gain (Hunger Pang, Hungry Coin). */
  hungerPerSpin: number;
  /** Multiplier on Hunger gain (Gluttony ×2, Flesh form ×0.8). */
  hungerGainX: number;
  /** Hunger can't go above this (Heart of the Machine). */
  hungerCap: number;
  /** Hunger can't go below this (the Maw's Grin before G_EYES). */
  hungerFloor: number;
  /** Coin/Money Bag don't pay outside patterns (Sticky Coins). */
  offPatternOff: boolean;
  hexDuration: number;
  cleanseMult: number;
  /** Multiplicative VALUE per tag / id (Rat Crown vermin ×2, Rot Pact fruit ×3). */
  valueXByTag: Partial<Record<Tag, number>>;
  valueXById: Record<string, number>;
  /** Every Nth spin of the run pays 0 (Borrowed Luck). 0 = off. */
  forcedDeadEvery: number;
  /** Each nudge costs this share of your coins (Pay in Blood). */
  nudgeCoinCost: number;
  /** Each spin costs this share of your coins (Bloody Nail). */
  spinCoinCost: number;
  /** Carried-over coins after a tribute × this (Infinite Receipt). */
  carryX: number;
  /** Coins at window start = this share of T(n) (Tip Jar). */
  windowStartCoinsPct: number;
  /** After a dead spin, gain this many Nudges (Fishhook). */
  deadSpinNudge: number;
  /** Patterns touching a held reel get +MULT (Anchor). */
  heldPatternMult: number;
  /** Patterns containing a Grin: ×MULT (Golden Grin). */
  grinPatternX: number;
  /** Every 4th spin: all patterns ×2 MULT (Metronome). */
  metronome: boolean;
  /** Rotten fruit matches as its fresh fruit (Snake Oil). */
  snakeOil: boolean;
  /** Black Cats eat at this distance (Cat Bell: 2). */
  catReach: number;
  /** Rats are wild (Rat King). */
  ratsWild: boolean;
  /** Symbols that are partly wild (Bone) are also wild for these ids (Wishbone adds rats). */
  extraWildFor: string[];
  /** Symbol ids the machine never eats (Fly Paper). */
  noEat: Set<string>;
  /** Dice: minimum roll (Loaded Die) and roll twice keep higher (Bone Dice). */
  diceMin: number;
  diceAdvantage: boolean;
  /** Moths always count as next to a Candle (Moth Lamp). */
  mothLamp: boolean;
  /** Candles don't melt while a Moth is adjacent (Zippo); never melt (Pocket Sun). */
  zippo: boolean;
  candleNoMelt: boolean;
  hourglassSpins: number;
  hourglassMax: number;
  /** Feeding: Hunger removed × this (Second Mouth). */
  feedX: number;
  /** Feeding a symbol grows a random deck symbol by this VALUE (Surgeon's Kit). */
  feedGrow: number;
  /** Feeding grants +1 spin next window, once per intermission (Chalice of Plenty). */
  feedSpin: boolean;
  /** Next draft offers +1 choice after feeding (Second Mouth). */
  feedDraftBonus: boolean;
  /** Blood Bank: unused Nudges at window end → ×MULT next window. */
  bloodBank: boolean;
  /** Shows reel 1's next stop before the pull (Crystal Ball). */
  crystalBall: boolean;
  /** Peek rows hidden and the odds panel disabled (Blindfold). */
  blind: boolean;
  /** Voodoo Doll: when a Hex lands, the leftmost charm triggers twice this spin. */
  voodoo: boolean;
  // Bonuses
  scratchSize: number;
  scratchBrush: number;
  scratchNoTrap: number;
  scratchGlow: boolean;
  hiloVisor: boolean;
  hiloHistory: number;
  hiloSafeRung: boolean;
  hiloTieWins: boolean;
  wheelBand: number;
  wheelStepX: number;
  nightShiftBonus: number;
  bonusPayoutX: number;
  sigilDraftX: number;
  grinIsSigil: boolean;
  /** Relic starts: a random Rare symbol in the starting deck (Glass Eye). */
  startRare: number;
  /** Keep one charm into the next run when Consumed (Mother's Locket). */
  locket: boolean;
  /** Last Meal: survive a failed tribute once per run by sacrificing the priciest charm. */
  lastMeal: boolean;
  /** Propositions offer this many deals at once (Fine Print Reader: 2). */
  dealChoices: number;
  /** Tribute Souls multiplier from deals (The Tithe −25%). */
  tributeSoulX: number;
  soulsPerReckoning: number;
  omenWindows: number;
  serendipity: number;
  midas: number;
  /** Vermin +VALUE per vermin visible (Vermin Economy). */
  verminEconomy: number;
  fateCap: number;
  /** Tribute 40 becomes The Last Supper (Cathedral). */
  lastSupper: boolean;
  /** The Hand upgrades owned: autospin, speed (level), autopay, autohold, autofeed, autonudge, autodraft, autobonus. */
  hand: Record<string, number>;
  /** Molt form passives. */
  jackpotFormX: number;
  /** Start-of-run tribute offset from the Deed (stacks with Head Start, up to Tribute 10). */
  startTributeAdd: number;
  // ── Eyes, Doom and Devour (docs/04 §14) ──
  doomMax: number;
  doomFloor: number;
  doomDecayX: number;
  devourChanceX: number;
  /** Teeth in one pattern that force a Devour (Dentures: 4). */
  devourTeeth: number;
  /** Chance a Devour becomes a MIRACLE (Saint's Knuckle). */
  miracleChance: number;
  /** Halo Fragment: once per window a Devour becomes a MIRACLE. */
  halofrag: boolean;
  /** Share of the payout a Devour keeps (Jar of Teeth). */
  devourKeep: number;
  /** ×MULT for the rest of the window per Devour (Jar of Teeth). */
  devourXMult: number;
  /** Eyes count this many times for their own VALUE (Glass Eye). */
  eyeCountX: number;
  // ── Teeth Drop and The Feast ──
  plinkoBalls: number;
  goldenPegs: number;
  goldenPegX: number;
  bucketWiden: number;
  feastRespins: number;
  feastStartMorsels: number;
}

export function emptyModifiers(): Modifiers {
  return {
    size: 'bandit',
    features: new Set(),
    bonuses: new Set(),
    patterns: new Set<PatternId>(['line3', 'bandit', 'gorge']),
    symbolGates: new Set<SymbolGate>(['start']),
    luck: 0,
    luckById: {},
    pityAfter: B.PITY_AFTER,
    hotHandLuck: 0,
    perfectPullLuck: B.PERFECT_PULL_LUCK,
    perfectPullAnyInput: false,
    perfectPullXMult: 1,
    secondChance: false,
    loadedFate: false,
    favoredLuckPer5: 0,
    firstSpinLuck: 0,
    peekRows: B.PEEK_ROWS,
    holdPerWindow: B.START_HOLD,
    nudgePerWindow: B.START_NUDGE,
    spinsPerWindow: 0,
    holdCarryMax: 0,
    freeNudgeChance: 0,
    longNudge: false,
    nudgeSight: false,
    heldValue: 0,
    deadSpinNudgeRefund: 0,
    valueAll: 0,
    valueByTag: {},
    valueById: {},
    pctByTag: {},
    pctByRarity: {},
    patternMultAdd: {},
    patternMultAll: 0,
    jackpotMultX: 1,
    xMult: [],
    payEarlyPct: B.PAY_EARLY_PCT,
    compoundPct: 0,
    tributeMult: 1,
    soulMult: 1,
    soulFlatPerTribute: 0,
    startCoinsT1: 0,
    startTribute: 1,
    draftChoices: B.DRAFT_CHOICES,
    rarityBoost: 0,
    skipDraftSouls: 0,
    charmSlots: 0,
    shopCharms: B.SHOP_CHARMS,
    shopDiscount: 0,
    startCommonCharm: false,
    hungerGainPct: 0,
    feedPct: 0,
    feedValuePar: 0,
    feedTierPar: 0,
    ravenousX: B.RAVENOUS_XMULT,
    pickyEater: false,
    contentLuck: 0,
    adoringWindows: B.ADORING_WINDOWS,
    eatXMult: 0,
    rotAgeAdd: 0,
    rotSlow: 1,
    compostChance: 0,
    rotNever: false,
    reckonings: false,
    ageExtraById: {},
    ageExtraByTag: {},
    halveExceptTags: null,
    sqrtXMult: false,
    mirror: false,
    darkReels: false,
    gluttonEats: false,
    spinsMult: 1,
    payEarlyX: 1,
    payEarlyDisabled: false,
    reckoningTributeMult: B.RECKONING_TRIBUTE_MULT,
    lockedReels: new Set(),
    invertNudge: false,
    hidePreview: false,
    hungerPerSpin: 0,
    hungerGainX: 1,
    hungerCap: B.HUNGER_MAX,
    hungerFloor: 0,
    offPatternOff: false,
    hexDuration: B.HEX_DURATION,
    cleanseMult: B.CLEANSE_MULT,
    valueXByTag: {},
    valueXById: {},
    forcedDeadEvery: 0,
    nudgeCoinCost: 0,
    spinCoinCost: 0,
    carryX: 1,
    windowStartCoinsPct: 0,
    deadSpinNudge: 0,
    heldPatternMult: 0,
    grinPatternX: 1,
    metronome: false,
    snakeOil: false,
    catReach: 1,
    ratsWild: false,
    extraWildFor: [],
    noEat: new Set(),
    diceMin: 0,
    diceAdvantage: false,
    mothLamp: false,
    zippo: false,
    candleNoMelt: false,
    hourglassSpins: 1,
    hourglassMax: B.HOURGLASS_MAX,
    feedX: 1,
    feedGrow: 0,
    feedSpin: false,
    feedDraftBonus: false,
    bloodBank: false,
    crystalBall: false,
    blind: false,
    voodoo: false,
    scratchSize: 3,
    scratchBrush: 1,
    scratchNoTrap: 0,
    scratchGlow: false,
    hiloVisor: false,
    hiloHistory: 0,
    hiloSafeRung: false,
    hiloTieWins: false,
    wheelBand: 1,
    wheelStepX: 1,
    nightShiftBonus: 0,
    bonusPayoutX: 1,
    sigilDraftX: 1,
    grinIsSigil: false,
    startRare: 0,
    locket: false,
    lastMeal: false,
    dealChoices: 1,
    tributeSoulX: 1,
    soulsPerReckoning: B.RECKONING_SOULS,
    omenWindows: 1,
    serendipity: 0,
    midas: 0,
    verminEconomy: 0,
    fateCap: B.FATE_CAP,
    lastSupper: false,
    hand: {},
    jackpotFormX: 1,
    startTributeAdd: 0,
    doomMax: B.DOOM_MAX,
    doomFloor: 0,
    doomDecayX: 1,
    devourChanceX: 1,
    devourTeeth: B.DEVOUR_TEETH,
    miracleChance: 0,
    halofrag: false,
    devourKeep: 0,
    devourXMult: 0,
    eyeCountX: 1,
    plinkoBalls: B.PLINKO_BALLS,
    goldenPegs: 0,
    goldenPegX: 2,
    bucketWiden: 0,
    feastRespins: B.FEAST_RESPINS,
    feastStartMorsels: 0,
  };
}

/** Molt forms are cumulative: each one past Brass adds Luck and keeps the earlier passives. */
function applyForm(m: Modifiers, form: MetaState['form']): void {
  const k = B.FORMS.indexOf(form);
  if (k <= 0) return;
  m.luck += k * B.FORM_LUCK;
  addTo(m.pctByTag, 'tooth', B.BONE_TOOTH_PCT);
  if (k >= 2) m.hungerGainX *= B.FLESH_HUNGER_X;
  if (k >= 3) m.jackpotFormX *= B.GILDED_JACKPOT_X;
}

/** Meta-only modifiers (Web nodes, Molt form, slotted relics). Used at run start and in the Web UI. */
export function metaModifiers(meta: MetaState): Modifiers {
  const m = emptyModifiers();
  for (const [id, level] of Object.entries(meta.nodes)) {
    if (level <= 0) continue;
    const def = NODES.get(id);
    def?.mods?.(m, level);
  }
  applyForm(m, meta.form ?? 'brass');
  for (const id of meta.relics?.slotted ?? []) if (id) RELICS.get(id)?.mods?.(m);
  for (const s of FED_SECRETS) if (meta.flags?.[s.flag]) m.valueById[s.defId] = (m.valueById[s.defId] ?? 0) + s.valueAdd;
  return m;
}

/** Charms that are active: only as many as there are slots (a lost slot silences the rightmost). */
export function activeCharms(run: RunState, slots: number): string[] {
  return run.charms.slice(0, Math.max(0, slots));
}

/**
 * Full modifiers for the current run: nodes, form, relics, then deals, charms in slot order,
 * collector rules and hexes.
 */
export function computeModifiers(meta: MetaState, run: RunState | null): Modifiers {
  const m = metaModifiers(meta);
  if (run) {
    m.size = run.size;
    applyDealMods(m, run);
    m.charmSlots = Math.min(B.CHARM_SLOTS_MAX, m.charmSlots);
    const charms = activeCharms(run, m.charmSlots);
    const ctx = { destroyed: run.stats?.destroyed ?? 0, flags: run.flags ?? {} };
    const applyCharm = (id: string) => {
      const def = CHARMS.get(id);
      def?.mods?.(m);
      def?.runMods?.(m, ctx);
    };
    charms.forEach(applyCharm);
    // Voodoo Doll: a hex landed this spin, so the leftmost charm triggers twice.
    if (m.voodoo && run.flags?.voodooSpin === run.spinIndex && charms[0]) applyCharm(charms[0]);
    const ruleCtx = { spinsUsed: run.window.spinsUsed };
    for (const r of activeRules(run)) r.mods?.(m, ruleCtx);
    for (const h of run.hexes ?? []) HEXES.get(h.id)?.mods?.(m, h);
  }
  return m;
}
